import { createValidationRules } from '../SourceCreate.service';
import {
  type DynamicFieldData,
  type SourceDetailResponse,
  SourceDialogMode
} from '../SourceCreate.types';
import type { ProcessFieldOptions } from './DataProvider.types';

/**
 * Checks if a field is the required DNS Filter prefix field.
 *
 * Business Rule: DNS Filter provider requires a mandatory prefix field
 * to organize logs by client/tenant. This overrides the field's default
 * nonMandatory setting.
 */
export const isDnsFilterPrefixField = (
  fieldName: string,
  parentName: string
): boolean => {
  return fieldName === 'prefix' && parentName === 'data_provider.DNS_Filter';
};

/**
 * Finds S3 provider that matches the current parent path.
 *
 * Business Rule: When editing an existing S3 provider, the ingest_from
 * date field must be disabled to prevent accidentally changing historical
 * data ingestion start points.
 */
export const findMatchingS3Provider = (
  selectedSourceDetails: SourceDetailResponse | null,
  parentName: string
) => {
  if (!selectedSourceDetails) return undefined;

  return selectedSourceDetails?.s3_data_providers?.find(
    (provider: Record<string, unknown>) => {
      const providerPath = `data_provider.${provider.name}`;
      return providerPath === parentName;
    }
  );
};

/**
 * Applies validation rules to a field when the data source is selected.
 *
 * @param field - The field configuration from the backend
 * @param parentName - The parent path for field naming (e.g., "data_provider.DNS_Filter")
 * @returns Field with validation rules and full field name
 */
export const applyFieldValidation = (
  field: DynamicFieldData,
  parentName: string
): DynamicFieldData => {
  const isDnsPrefix = isDnsFilterPrefixField(field.name, parentName);

  // DNS Filter prefix is always required, even if marked nonMandatory
  const isRequired = isDnsPrefix ? false : field.nonMandatory;
  const validations = isDnsPrefix
    ? [
        ...(field.validations ?? []),
        { type: 'required', params: ['Prefix is Required'] }
      ]
    : field.validations;

  return {
    ...field,
    nonMandatory: isRequired,
    name: `${parentName}.${field.name}`,
    config: createValidationRules(validations)
  };
};

/**
 * Applies S3 provider-specific rules to fields.
 *
 * Business Rule: If an S3 provider already exists in Edit mode, the ingest_from
 * field must be disabled to prevent changing the historical ingestion start date.
 * This protects against accidental data gaps or duplication.
 *
 * @param field - The field to check
 * @param s3Provider - The matching S3 provider, if any
 * @returns Field with updated disabled state
 */
export const applyS3ProviderRules = (
  field: DynamicFieldData,
  s3Provider: Record<string, unknown> | undefined
): DynamicFieldData => {
  if (s3Provider && field.name.includes('ingest_from')) {
    return { ...field, disabled: true };
  }
  return { ...field, disabled: false };
};

/**
 * Removes validation from a field when the data source is unselected.
 *
 * @param field - The field to clear validation from
 * @param parentName - The parent path for field naming
 * @returns Field with no validation rules
 */
export const removeFieldValidation = (
  field: DynamicFieldData,
  parentName: string
): DynamicFieldData => {
  return {
    ...field,
    name: `${parentName}.${field.name}`,
    nonMandatory: true, // All fields optional when unselected
    validations: [],
    config: {} // No validation rules
  };
};

/**
 * Applies Microsoft-specific field rules in Edit mode.
 *
 * Business Rules:
 * 1. Active toggle: Only enable if certificate thumbprint exists (new cert flow)
 * 2. Ingest From field: Disable for existing providers to prevent changing historical data
 */
export const applyMicrosoftEditRules = (
  field: DynamicFieldData,
  mode: string,
  selectedSourceDetails: SourceDetailResponse | null,
  dataSourceName: string,
  isActivateToggleEnabledFn: (details: SourceDetailResponse) => boolean
): DynamicFieldData => {
  if (mode !== SourceDialogMode.Edit) return field;

  let { disabled } = field;

  // Rule 1: Enable active toggle only when certificate is properly configured
  if (field.name.includes('active') && selectedSourceDetails) {
    disabled = !isActivateToggleEnabledFn(selectedSourceDetails);
  }

  // Rule 2: Disable ingest_from for existing Microsoft providers
  if (field.name.includes('ingest_from') && selectedSourceDetails) {
    const providerExists = selectedSourceDetails?.ms_data_providers?.some(
      (provider) => provider?.connection_inputs?.name === dataSourceName
    );
    if (providerExists) disabled = true;
  }

  return { ...field, disabled };
};

/**
 * Processes a single field through all applicable business rules.
 *
 * This is the main pipeline function that orchestrates all field transformations:
 * 1. Apply validation rules if selected
 * 2. Apply S3-specific rules
 * 3. Apply Microsoft-specific rules (Edit mode only)
 *
 * @param field - The base field configuration
 * @param options - Context needed for processing (selection state, mode, providers, etc.)
 * @returns Fully processed field ready for rendering
 */
export const processField = (
  field: DynamicFieldData,
  options: ProcessFieldOptions
): DynamicFieldData => {
  const {
    isSelected,
    parentName,
    mode,
    selectedSourceDetails,
    dataSourceName,
    isActivateToggleEnabledFn
  } = options;

  const s3Provider = findMatchingS3Provider(selectedSourceDetails, parentName);

  // Step 1: Apply or remove validation based on selection
  let processedField = isSelected
    ? applyFieldValidation(field, parentName)
    : removeFieldValidation(field, parentName);

  // Step 2: Apply S3 provider rules
  processedField = applyS3ProviderRules(processedField, s3Provider);

  // Step 3: Apply Microsoft-specific rules (Edit mode only)
  processedField = applyMicrosoftEditRules(
    processedField,
    mode,
    selectedSourceDetails,
    dataSourceName,
    isActivateToggleEnabledFn
  );

  return processedField;
};

/**
 * Processes all fields for a data source through the field processing pipeline.
 *
 * @param fields - Array of field configurations
 * @param options - Processing context
 * @returns Array of fully processed fields
 */
export const processAllFields = (
  fields: DynamicFieldData[] | undefined,
  options: ProcessFieldOptions
): DynamicFieldData[] => {
  if (!fields) return [];

  return fields.map((field) => processField(field, options));
};

/**
 * Determines if a field should be marked as required in the UI.
 *
 * @param field - The field to check
 * @returns true if field is required (not marked as nonMandatory)
 */
export const isFieldRequired = (field: DynamicFieldData): boolean => {
  return !field.nonMandatory;
};
